import { supabase } from './supabase';
import { referralService } from './referralService';

export const subscriptionService = {
  async getUserSubscription(userId: string) {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return data;
  },

  async createSubscription(
    userId: string,
    planType: string,
    billingCycle: 'weekly' | 'monthly' | 'yearly',
    price: number
  ) {
    const now = new Date();
    let nextBillingDate = new Date(now);

    switch (billingCycle) {
      case 'weekly':
        nextBillingDate.setDate(now.getDate() + 7);
        break;
      case 'monthly':
        nextBillingDate.setMonth(now.getMonth() + 1);
        break;
      case 'yearly':
        nextBillingDate.setFullYear(now.getFullYear() + 1);
        break;
    }

    const { data, error } = await supabase
      .from('subscriptions')
      .insert({
        user_id: userId,
        plan_type: planType,
        billing_cycle: billingCycle,
        status: 'active',
        current_period_start: now.toISOString(),
        current_period_end: nextBillingDate.toISOString(),
        next_billing_date: nextBillingDate.toISOString(),
        price: price,
        cancel_at_period_end: false,
        retention_discount_applied: false,
      })
      .select()
      .single();

    if (error) throw error;

    await referralService.awardSubscriptionCredits(userId);

    return data;
  },

  async cancelSubscription(userId: string, immediately = false) {
    const subscription = await this.getUserSubscription(userId);

    if (!subscription) {
      throw new Error('No active subscription found');
    }

    if (immediately) {
      const { error } = await supabase
        .from('subscriptions')
        .update({
          status: 'cancelled',
          cancel_at_period_end: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', subscription.id);

      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('subscriptions')
        .update({
          cancel_at_period_end: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', subscription.id);

      if (error) throw error;
    }
  },

  async acceptRetentionOffer(userId: string) {
    const subscription = await this.getUserSubscription(userId);

    if (!subscription) {
      throw new Error('No active subscription found');
    }

    const discountedPrice = subscription.price * 0.5;

    const { error } = await supabase
      .from('subscriptions')
      .update({
        retention_discount_applied: true,
        retention_discount_accepted_at: new Date().toISOString(),
        price: discountedPrice,
        cancel_at_period_end: false,
        updated_at: new Date().toISOString(),
      })
      .eq('id', subscription.id);

    if (error) throw error;

    return { discountedPrice };
  },

  async reactivateSubscription(userId: string) {
    const subscription = await this.getUserSubscription(userId);

    if (!subscription) {
      throw new Error('No active subscription found');
    }

    const { error } = await supabase
      .from('subscriptions')
      .update({
        cancel_at_period_end: false,
        status: 'active',
        updated_at: new Date().toISOString(),
      })
      .eq('id', subscription.id);

    if (error) throw error;
  },

  calculatePrice(basePrice: number, billingCycle: 'weekly' | 'monthly' | 'yearly'): number {
    switch (billingCycle) {
      case 'weekly':
        return Number((basePrice / 4).toFixed(2));
      case 'monthly':
        return basePrice;
      case 'yearly':
        return Number((basePrice * 12 * 0.84).toFixed(2));
      default:
        return basePrice;
    }
  },

  calculateSavings(basePrice: number, _billingCycle: 'yearly'): number {
    const yearlyPrice = this.calculatePrice(basePrice, 'yearly');
    const monthlyTotal = basePrice * 12;
    return Number((monthlyTotal - yearlyPrice).toFixed(2));
  },
};
